import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Box, Group, Loader, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { GAME_TYPES, VOLATILITIES, type Volatility } from '@seo/contracts';
import { CreatePanel } from '../components/CreatePanel';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { zodResolver } from '../lib/zod-resolver';
import { DataTable, type Column } from '../components/DataTable';
import { GameEditor } from './GameEditor';
import classes from './EntityPage.module.css';

interface GameRow {
  id: string;
  name: string;
  providerId: string;
  providerName: string;
  type: string;
  releaseYear: number | null;
  rtpBps: number | null;
  volatility: string | null;
  maxWinMultiplier: number | null;
  coreReady: boolean;
  mediaReady: boolean;
  keywordCount: number;
  sitesCount: number;
}

const TYPE_LABELS: Record<string, string> = {
  slot: 'слот',
  crash: 'crash',
  live: 'live',
  arcade: 'аркада',
  table: 'настільна',
};

/**
 * Typed by the enum, not by `string`: a value added to `VOLATILITIES` without a caption used
 * to reach the picker as its raw wire value (`very_high` among «низька/середня/висока»).
 * `Record<Volatility, string>` makes that a compile error instead of a screen defect.
 */
const VOLATILITY_LABELS: Record<Volatility, string> = {
  low: 'низька',
  medium: 'середня',
  high: 'висока',
  very_high: 'дуже висока',
};

/** Rows arrive as strings; anything outside the enum prints as itself rather than as a blank. */
const volatilityLabel = (value: string): string => {
  const known = VOLATILITIES.find((v) => v === value);
  return known === undefined ? value : VOLATILITY_LABELS[known];
};

/**
 * The picker hands back `string | null`; the form field holds an enum value or nothing.
 * Checked rather than asserted — `as Volatility` would silence the compiler exactly at the
 * boundary where the string stops being arbitrary.
 */
const parseVolatility = (value: string | null): '' | Volatility =>
  VOLATILITIES.find((v) => v === value) ?? '';

/** The form is the only place a percentage exists; the wire and the column carry basis points. */
const RTP_BPS_PER_PERCENT = 100;
const RTP_MIN_PERCENT = 0;
const RTP_MAX_PERCENT = 100;

/** RTP is stored in basis points; the screen is the only place it becomes a percentage. */
const formatRtp = (bps: number | null): string =>
  bps === null ? '—' : `${(bps / RTP_BPS_PER_PERCENT).toFixed(2)}%`;

/** Only `name`, `providerId` and `type` are required by `gameInput` (apps/api/src/routers/
 * games.ts) — the rest stays out of the create form so it doesn't grow past a one-row panel. */
const createGameSchema = z.object({
  // Trimmed before the length check, and trimmed again at submit: « Aurora » and «Aurora» are
  // one game to an operator, and the server refuses the duplicate on the trimmed name.
  name: z.string().trim().min(1, 'Вкажіть назву гри').max(200),
  providerId: z.string().min(1, 'Оберіть провайдера'),
  type: z.enum(GAME_TYPES),
  // Empty means «ще не задокументовано» — a value, not a missing one.
  volatility: z.literal('').or(z.enum(VOLATILITIES)),
  // Text on purpose: the operator types a percentage, the wire carries basis points —
  // converted at submit, same as `rtpPercentToBps` on the core side. Перевіряється числом
  // саме тут, бо `Number('дев’яносто')` — це `NaN`: superjson передає `NaN` дослівно, і
  // сервер відповідає загальною 400 від zod. Форма пояснює причину там, де оператор ще
  // бачить, що набрав.
  rtp: z.string().superRefine((raw, ctx) => {
    const value = raw.trim();
    if (value === '') return;
    const percent = Number(value);
    if (!Number.isFinite(percent)) {
      ctx.addIssue('RTP — число у відсотках, напр. 96.5');
      return;
    }
    if (percent < RTP_MIN_PERCENT || percent > RTP_MAX_PERCENT) {
      ctx.addIssue(`RTP — відсоток від ${String(RTP_MIN_PERCENT)} до ${String(RTP_MAX_PERCENT)}`);
    }
  }),
});

type CreateGameValues = z.infer<typeof createGameSchema>;

const CREATE_GAME_INITIAL: CreateGameValues = {
  name: '',
  providerId: '',
  type: 'slot',
  volatility: '',
  rtp: '',
};

/**
 * База слотів (§1.4).
 *
 * Two independent gates decide whether a game can be built on, and the registry shows them
 * as two columns rather than one verdict: «документація є, картинок немає» is a worklist for
 * the design team, and collapsing it into a single red dot would hide who is blocked.
 */
export function GamesPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [readyOnly, setReadyOnly] = useState(false);
  const [missingMediaOnly, setMissingMediaOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const list = useQuery(
    trpc.games.list.queryOptions({
      ...(search ? { search } : {}),
      ...(readyOnly ? { readyOnly: true } : {}),
      ...(missingMediaOnly ? { missingMediaOnly: true } : {}),
      pagination: { limit: 200, direction: 'asc' },
    }),
  );

  const detail = useQuery({
    ...trpc.games.byId.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  // Provider is a reference to gameProviders — the create form offers a picker, never a
  // raw uuid field. Same zone (`databases`, read) as the games list, so no extra RBAC gap.
  const providers = useQuery(
    trpc.reference.providers.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );

  const createForm = useForm<CreateGameValues>({
    initialValues: CREATE_GAME_INITIAL,
    validate: zodResolver(createGameSchema),
  });

  const createGame = useMutation(
    trpc.games.create.mutationOptions({
      onSuccess: async () => {
        createForm.reset();
        setCreateOpen(false);
        await queryClient.invalidateQueries({ queryKey: trpc.games.pathKey() });
      },
    }),
  );

  const columns: Column<GameRow>[] = [
    { key: 'name', header: 'Гра', render: (row) => <span>{row.name}</span> },
    {
      key: 'provider',
      header: 'Провайдер',
      render: (row) => <span className={classes.dim}>{row.providerName}</span>,
    },
    {
      key: 'type',
      header: 'Тип',
      render: (row) => (
        <Badge size="sm" variant="default" data-testid={`game-type-${row.name}`}>
          {TYPE_LABELS[row.type] ?? row.type}
        </Badge>
      ),
    },
    {
      key: 'rtp',
      header: 'RTP',
      align: 'right',
      render: (row) => (
        <span className={classes.mono} data-testid={`game-rtp-${row.name}`}>
          {formatRtp(row.rtpBps)}
        </span>
      ),
    },
    {
      key: 'volatility',
      header: 'Волатильність',
      render: (row) => (
        <span className={classes.dim}>
          {row.volatility === null ? '—' : volatilityLabel(row.volatility)}
        </span>
      ),
    },
    {
      // Two gates, two badges: the design team and the content team are blocked by different
      // things and each needs to see its own.
      key: 'ready',
      header: 'Готовність',
      render: (row) => (
        <Group gap={6}>
          <Badge
            size="sm"
            color={row.coreReady ? 'mint' : 'rose'}
            // Colour alone is not a testable (or accessible) signal — the state is in the
            // DOM as well, so a screen reader and a test see the same thing an eye does.
            data-ready={row.coreReady}
            data-testid={`game-core-${row.name}`}
          >
            дані
          </Badge>
          <Badge
            size="sm"
            color={row.mediaReady ? 'mint' : 'rose'}
            data-ready={row.mediaReady}
            data-testid={`game-media-${row.name}`}
          >
            медіа
          </Badge>
        </Group>
      ),
    },
    {
      key: 'sites',
      header: 'Сайтів',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.sitesCount}</span>,
    },
  ];

  const game = detail.data;
  const providerOptions = (providers.data?.items ?? []).map((p) => ({
    value: p.id,
    label: p.name,
  }));
  const volatilityOptions = [
    { value: '', label: '—' },
    ...VOLATILITIES.map((v) => ({ value: v, label: VOLATILITY_LABELS[v] })),
  ];

  return (
    <Stack gap="md">
      <Group gap="md">
        <TextInput
          placeholder="гра…"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
          }}
          data-testid="game-search"
          style={{ maxWidth: 240 }}
        />
        <Switch
          size="xs"
          label="Лише готові до збірки"
          checked={readyOnly}
          onChange={(e) => {
            setReadyOnly(e.currentTarget.checked);
          }}
          data-testid="game-ready-only"
        />
        <Switch
          size="xs"
          label="Бракує медіа"
          checked={missingMediaOnly}
          onChange={(e) => {
            setMissingMediaOnly(e.currentTarget.checked);
          }}
          data-testid="game-missing-media"
        />
        <Text className={classes.dim} data-testid="game-count">
          {list.data ? `${String(list.data.items.length)} ігор` : ''}
        </Text>
      </Group>

      <CreatePanel
        onReset={() => {
          createGame.reset();
          createForm.reset();
        }}
        zone="databases"
        label="Додати гру"
        testId="game"
        open={createOpen}
        onOpenChange={setCreateOpen}
        busy={createGame.isPending}
        error={createGame.error}
        onSubmit={createForm.onSubmit((values) => {
          const rtp = values.rtp.trim();
          createGame.mutate({
            name: values.name.trim(),
            providerId: values.providerId,
            type: values.type,
            // Absent, not null: an unset volatility and an unset RTP mean «not documented yet».
            ...(values.volatility === '' ? {} : { volatility: values.volatility }),
            // The operator types a percentage; the wire carries basis points.
            ...(rtp === '' ? {} : { rtpBps: Math.round(Number(rtp) * RTP_BPS_PER_PERCENT) }),
          });
        })}
      >
        <TextInput
          size="xs"
          placeholder="назва гри"
          data-testid="create-game-name"
          style={{ width: 220 }}
          {...createForm.getInputProps('name')}
        />
        <Select
          size="xs"
          placeholder={providers.error === null ? 'провайдер' : 'список недоступний'}
          searchable
          data={providerOptions}
          data-testid="create-game-provider"
          style={{ width: 200 }}
          {...createForm.getInputProps('providerId')}
        />
        {/* An empty picker reads as «провайдерів немає»; a failed request is a different
            situation with a different next step, so it says so instead of staying silent. */}
        {providers.error === null ? null : (
          <Text className={classes.danger} role="alert" data-testid="create-game-providers-error">
            Список провайдерів не завантажився: {publicMessageOf(providers.error)}
          </Text>
        )}
        <Select
          size="xs"
          data={GAME_TYPES.map((t) => ({ value: t, label: TYPE_LABELS[t] ?? t }))}
          allowDeselect={false}
          data-testid="create-game-type"
          style={{ width: 130 }}
          {...createForm.getInputProps('type')}
        />
        <Select
          size="xs"
          placeholder="волатильність"
          data={volatilityOptions}
          value={createForm.values.volatility}
          onChange={(value) => {
            createForm.setFieldValue('volatility', parseVolatility(value));
          }}
          error={createForm.errors.volatility}
          data-testid="create-game-volatility"
          style={{ width: 150 }}
        />
        <TextInput
          size="xs"
          placeholder="RTP %"
          data-testid="create-game-rtp"
          style={{ width: 90 }}
          {...createForm.getInputProps('rtp')}
        />
      </CreatePanel>

      <DataTable
        rows={list.data?.items ?? []}
        columns={columns}
        loading={list.isPending}
        error={list.error}
        emptyMessage="Ігор немає. Імпортуйте каталог провайдера або створіть гру вручну."
        onRowClick={(row) => {
          setSelectedId(row.id);
        }}
        rowTestId={(row) => `game-row-${row.name}`}
        testId="game-table"
      />

      {game === undefined ? null : (
        // Повний рядок `getGame()`, а не вибрані чотири поля: картка редагує все, чим
        // володіє таблиця `games`, а не лише те, що показував перший варіант екрана.
        <GameEditor key={game.id} row={game} onClose={() => { setSelectedId(null); }} />
      )}

      {selectedId === null ? null : (
        <Box className={classes.panel} data-testid="game-detail">
          {detail.isPending ? (
            <Loader size="sm" />
          ) : game === undefined ? null : (
            <Stack gap="md">
              <Group justify="space-between">
                <Text className={classes.panelTitle}>{game.name}</Text>
                <Group gap={8}>
                  <Badge
                    size="sm"
                    color={game.readiness.coreReady && game.readiness.mediaReady ? 'mint' : 'rose'}
                    data-testid="game-detail-ready"
                  >
                    {game.readiness.coreReady && game.readiness.mediaReady
                      ? 'готова до збірки'
                      : 'збірка заблокована'}
                  </Badge>
                </Group>
              </Group>

              <Group gap="xl" align="flex-start">
                <Box>
                  <Text className={classes.dim} mb={4}>
                    Дані
                  </Text>
                  {game.readiness.missingCore.length === 0 ? (
                    <Text className={classes.mono} data-testid="game-core-ok">
                      ✓ повні
                    </Text>
                  ) : (
                    <Stack gap={2}>
                      {game.readiness.missingCore.map((label) => (
                        <Text
                          key={label}
                          className={classes.danger}
                          data-testid={`missing-${label}`}
                        >
                          ✕ {label}
                        </Text>
                      ))}
                    </Stack>
                  )}
                </Box>

                <Box>
                  <Text className={classes.dim} mb={4}>
                    Медіа
                  </Text>
                  {game.readiness.missingMedia.length === 0 ? (
                    <Text className={classes.mono} data-testid="game-media-ok">
                      ✓ повні
                    </Text>
                  ) : (
                    <Stack gap={2}>
                      {game.readiness.missingMedia.map((label) => (
                        <Text
                          key={label}
                          className={classes.danger}
                          data-testid={`missing-${label}`}
                        >
                          ✕ {label}
                        </Text>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Group>

              <Box>
                <Text className={classes.dim} mb={6}>
                  Слоти медіа
                </Text>
                <Group gap={6}>
                  {game.assets.map((asset) => (
                    <Badge
                      key={asset.slot}
                      size="sm"
                      variant={asset.mediaFileId === null ? 'default' : 'filled'}
                      color={asset.mediaFileId === null ? 'rose' : 'mint'}
                      data-testid={`game-asset-${asset.slot}`}
                    >
                      {asset.slot}
                    </Badge>
                  ))}
                </Group>
              </Box>

              <Group gap="lg">
                <Text className={classes.dim}>
                  RTP <span className={classes.mono}>{formatRtp(game.rtpBps)}</span>
                </Text>
                <Text className={classes.dim}>
                  максимальний виграш{' '}
                  <span className={classes.mono}>
                    {game.maxWinMultiplier === null ? '—' : `×${String(game.maxWinMultiplier)}`}
                  </span>
                </Text>
                <Text className={classes.dim}>
                  лінії <span className={classes.mono}>{game.linesOrWays ?? '—'}</span>
                </Text>
                <Text className={classes.dim} data-testid="game-demo">
                  демо {game.demoIframeUrl === null ? '—' : 'є'}
                </Text>
              </Group>
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  );
}
